/**
 * debugUnlock — 더보기의 `앱 버전` 줄을 연타해 여는 **개발자 옵션** 잠금.
 *
 * 계측 오버레이는 실기기 앞의 사람이 켜야 한다. 예전에는 URL 질의(`?probe=1`)와 빌드
 * 종류(dev 면 기본 ON)로 켰는데, 그 빌드로 리뷰하면 측정 화면마다 검은 계측 바가 버튼을
 * 덮었고 끄는 자리가 화면 안에 없었다(TestFlight 3차 #103). 이제 여기가 유일한 입구다.
 *
 * 왜 연타냐: 일반 사용자가 우연히 밟지 않으면서, 기기 앞의 사람이 아무 도구 없이 열 수
 * 있어야 한다. 모바일 앱의 관례(버전 연타)를 그대로 쓴다.
 *
 * 순수 함수 + localStorage 한 겹 — React·IO 의존 없음(vitest 대상). 저장이 실패해도
 * 무해하다: 못 읽으면 잠긴 것이다(사파리 프라이빗 등).
 */

const STORAGE_KEY = 'muzly.debugMenu';

/** 열리기까지 필요한 탭 수. 관례값이고, 우연히 밟기엔 충분히 많다. */
export const UNLOCK_TAPS = 10;

/**
 * 연타로 인정하는 탭 사이 간격(ms). 이보다 뜸하면 **연타가 아니라** 다른 볼일이라
 * 세기를 처음부터 다시 한다 — 안 그러면 며칠에 걸친 우연한 탭이 쌓여 열린다.
 */
export const TAP_WINDOW_MS = 1_500;

export interface TapState {
  /** 지금까지 이어진 탭 수. */
  count: number;
  /** 마지막 탭 시각(ms). */
  atMs: number;
}

export const NO_TAPS: TapState = { count: 0, atMs: 0 };

/**
 * 탭 하나를 센다. 창 안이면 이어 세고, 밖이면 이번 탭이 첫 탭이다.
 *
 * `unlocked`는 **이번 탭으로 열렸는가**다 — 이미 열린 상태를 다시 말하지 않는다.
 * 그래서 호출부는 이 값만 보고 "열렸어요" 안내를 한 번만 낸다.
 */
export const tap = (
  state: TapState,
  nowMs: number,
): { state: TapState; unlocked: boolean } => {
  const continued = nowMs - state.atMs <= TAP_WINDOW_MS;
  const count = (continued ? state.count : 0) + 1;
  if (count >= UNLOCK_TAPS) {
    // 열린 뒤에는 세기를 비운다 — 이어 누르는 탭이 다시 `unlocked`를 내면 안내가 반복된다.
    return { state: NO_TAPS, unlocked: true };
  }
  return { state: { count, atMs: nowMs }, unlocked: false };
};

/**
 * 잠금이 바뀌었다는 알림. 이 값을 렌더에 쓰는 컴포넌트가 더보기 화면 **밖에도** 있어서
 * (`DevToolLauncher`, 루트에 상주) 스토리지만 보면 라우트가 바뀔 때까지 옛 상태로 남는다 —
 * 잠갔는데 DEV 버튼이 그대로 떠 있는 것이 바로 이 PR 이 고치려던 증상이다.
 *
 * 다른 탭의 변경(`storage`)도 같이 받는다 — 브라우저에서 두 탭을 열어 둔 개발 상황.
 */
const EVENT = 'muzly:debugMenu';

export const subscribeDebugUnlock = (onChange: () => void): (() => void) => {
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

export const isDebugUnlocked = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const setDebugUnlocked = (on: boolean): void => {
  try {
    if (on) {
      localStorage.setItem(STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* 저장 못 하면 이번 화면에서만 산다 */
  }
  window.dispatchEvent(new Event(EVENT));
};
